import React, { useMemo } from "react";
import { Box, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import { PandasDataFrameProto } from "build-ts/dataframe";
import { buildPandasTableFormatContext, getFormattedPandasCellValue } from "../utils/pandasTableUtils";

interface PandasTableProps {
  df?: PandasDataFrameProto;
  columnTooltips?: Record<string, React.ReactNode>;
}

/**
 * Renders a PandasDataFrameProto as a formatted table.
 * Uses format_map and column_dtypes for cell-level formatting.
 * Pure component — no state or context access.
 */
const PandasTable: React.FC<PandasTableProps> = ({ df, columnTooltips }) => {
  const formatContext = useMemo(() => buildPandasTableFormatContext(df), [df]);
  const { columns, rows, columnMeta, rowMeta } = formatContext;

  if (!df || columns.length === 0) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No data available.
        </Text>
      </Box>
    );
  }

  const tablePad = { base: 1, md: 2 } as const;

  return (
    <Box w="100%">
      <Box overflowX="auto" px={tablePad}>
        <Table size="sm" variant="simple" w="100%">
          <Thead bg="gray.50" position="sticky" top={0} boxShadow="sm">
            <Tr>
              {columnMeta.map(({ col, align }, colIndex) => {
                const tip = columnTooltips?.[col];
                return (
                  <Th
                    key={`${col}-${colIndex}`}
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.900"
                    textTransform="none"
                    letterSpacing="normal"
                    borderColor="gray.200"
                    px={3}
                    py={2}
                    whiteSpace="nowrap"
                    textAlign={align}
                  >
                    {tip ? (
                      <Tooltip label={tip} hasArrow placement="bottom" openDelay={100}>
                        <Text
                          as="span"
                          display="inline-block"
                          mb={0}
                          fontSize="inherit"
                          fontWeight="inherit"
                          color="inherit"
                          cursor="default"
                          borderBottom="1px dashed"
                          borderColor="gray.400"
                          _hover={{ borderColor: "purple.400", color: "purple.700" }}
                        >
                          {col}
                        </Text>
                      </Tooltip>
                    ) : (
                      col
                    )}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, rowIdx) => (
              <Tr key={rowIdx} _hover={{ bg: "gray.50" }} transition="background 0.1s">
                {columnMeta.map(({ col, align }, colIndex) => {
                  const rowCell = rowMeta[rowIdx]?.[col];
                  const cellValue = getFormattedPandasCellValue(formatContext, rowIdx, colIndex, row[col]);
                  const cellAlign = rowCell ? rowCell.align : align;

                  return (
                    <Td
                      key={`${col}-${rowIdx}-${colIndex}`}
                      fontSize="0.8rem"
                      color="gray.700"
                      borderColor="gray.100"
                      px={3}
                      py={1.5}
                      whiteSpace="normal"
                      fontWeight="600"
                      textAlign={cellAlign}
                    >
                      <Tooltip label={cellValue} placement="top" hasArrow openDelay={300}>
                        <Text
                          as="span"
                          display="block"
                          mb={0}
                          fontSize="inherit"
                          color="inherit"
                          fontWeight="inherit"
                          textAlign="inherit"
                        >
                          {cellValue}
                        </Text>
                      </Tooltip>
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default PandasTable;
